import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as trackingAPI from '../../api/dashboardAPI/trackingAPI';

export const fetchWorkflowTracking = createAsyncThunk(
    'pendingTracking/fetchWorkflowTracking',
    async (params, { rejectWithValue }) => {
        try {
            return await trackingAPI.getWorkflowTracking(params);
        } catch (error) {
            return rejectWithValue(error.message || 'Failed');
        }
    }
);

const pendingTrackingSlice = createSlice({
    name: 'pendingTracking',
    initialState: {
        rows: [],
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchWorkflowTracking.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWorkflowTracking.fulfilled, (state, action) => {
                state.loading = false;
                state.rows = action.payload?.Data || [];
            })
            .addCase(fetchWorkflowTracking.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.rows = [];
            });
    },
});

export const selectTrackingRows    = (state) => state.pendingTracking.rows;
export const selectTrackingLoading = (state) => state.pendingTracking.loading;

// One entry per workflow (MOID): its approval chain (levels in order, with counts) and pending records.
// A workflow can carry several inbox categories (e.g. MRR + Multi MRR); the label uses the category
// when there is only one, else the workflow's display name.
export const buildTrackingTypes = (rows) => {
    const byMoid = new Map();
    rows.forEach((r) => {
        if (!byMoid.has(r.MOID)) byMoid.set(r.MOID, { moid: r.MOID, moduleName: r.ModuleName, levels: [], items: [] });
        const t = byMoid.get(r.MOID);
        if (r.RowType === 'Level') t.levels.push(r);
        else t.items.push(r);
    });
    return Array.from(byMoid.values())
        .filter((t) => t.items.length > 0)
        .map((t) => {
            const categories = [...new Set(t.items.map((i) => (i.ModuleDisplayName || '').trim()).filter(Boolean))];
            const levels = [...t.levels].sort((a, b) => (a.LevelOfApproval ?? 0) - (b.LevelOfApproval ?? 0));
            // pending roles outside the active chain (WorkFlowLevels Status != 1) still show their items
            t.items.forEach((i) => {
                if (!levels.some((l) => l.PendingRoleId === i.PendingRoleId)) {
                    levels.push({ ...i, RowType: 'Level', PendingCount: t.items.filter((x) => x.PendingRoleId === i.PendingRoleId).reduce((s, x) => s + x.PendingCount, 0) });
                }
            });
            return {
                ...t,
                levels,
                categories,
                label: categories.length === 1 ? categories[0] : (t.moduleName || '').trim(),
                total: t.items.reduce((s, i) => s + (i.PendingCount || 0), 0),
                mine: t.items.filter((i) => i.IsMine).reduce((s, i) => s + (i.PendingCount || 0), 0),
            };
        })
        .sort((a, b) => b.mine - a.mine || a.label.localeCompare(b.label));
};

export default pendingTrackingSlice.reducer;
